import React, { lazy, Suspense, useEffect, useState, Fragment } from 'react'
import { withRouter } from 'react-router-dom'
import { Alert, Layout, Spin, Row, Button } from 'antd'
import { useDispatch } from 'react-redux'
import { appSelector } from '../helpers/appSelector'
import { AppDispatch } from '../helpers/appDispatch'
import { MerchantData } from '../interfaces'
import {
  getMerchantsRequest,
  exportViewPendingTxRequest,
  viewPendingTransactionsRequest,
  clearData
} from '../store/reports'
import moment from 'moment'
import { isEmpty } from '../helpers/isEmpty'
import { useTranslation } from 'react-i18next'
import { roles } from '../helpers/constants'

const { Content } = Layout

const PendingFilters = lazy(() => import('../components/pending-transactions/PendingFilters'))
const Details = lazy(() => import('../components/pending-transactions/Details'))
const CurrencyFilter = lazy(() => import('../components/fee-reports/CurrencyFilter'))
const EmptyBox = lazy(() => import('../components/transactions/EmptyBox'))

const PendingTransactionsView = () => {
  const dispatch: AppDispatch = useDispatch()
  const { t } = useTranslation()
  const reports = appSelector((state) => state.reports)
  const { user } = appSelector((state) => state.auth)
  const [merchant, setMerchant] = useState<MerchantData | null>(null)
  const [periodFrom, setPeriodFrom] = useState('')
  const [periodTo, setPeriodTo] = useState('')
  const [exportType, setExportType] = useState('')
  const [pageSize, setPageSize] = useState(10)
  const [skip, setSkip] = useState(0)
  const [currency, setCurrency] = useState('USD')
  const [isSearching, setIsSearching] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const {
    merchants,
    pendingtransactions,
    loading,
    isExporting,
    pendingError,
    pendingExportError
  } = reports
  const pendingData =
    pendingtransactions?.records ||
    pendingtransactions?.Records ||
    pendingtransactions?.data ||
    []
  const pendingSummary =
    pendingtransactions?.summary || pendingtransactions?.Summary
  const total =
    pendingSummary?.recordsTotal ||
    pendingSummary?.RecordsTotal ||
    pendingtransactions?.total?.value ||
    pendingData.length

  let isSuperMerchant = false
  if (user) {
    const superMerchantRole = user.roles.find((r) => r === roles.SuperMerchant)
    isSuperMerchant = superMerchantRole !== undefined
  }

  const buildParams = ({
    nextCurrency = currency,
    nextPageSize = pageSize,
    nextSkip = skip,
    nextPeriodFrom = periodFrom,
    nextPeriodTo = periodTo,
    nextMerchant = merchant,
    nextExportType = ''
  } = {}) => ({
    currency: nextCurrency,
    pageSize: nextPageSize,
    skip: nextSkip,
    periodFrom: nextPeriodFrom,
    periodTo: nextPeriodTo,
    merchant: nextMerchant ? nextMerchant.name : '',
    exportType: nextExportType,
    status: '',
    channel: '',
    searchValue: '',
    fixedPeriod: 'overall'
  })

  const getErrorMessage = (error: any) => {
    if (typeof error === 'string') return error
    return error?.detail || error?.message || 'An unknown error occurred'
  }

  useEffect(() => {
    const { merchants } = reports
    if (isEmpty(merchants)) {
      dispatch(getMerchantsRequest())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reloadReport = () => {
    dispatch(viewPendingTransactionsRequest(buildParams()))
  }

  const onSelectCurrency = (value: string) => {
    setCurrency(value)
    setSkip(0)
    setCurrentPage(1)

    if (isSearching) {
      dispatch(
        viewPendingTransactionsRequest(
          buildParams({ nextCurrency: value, nextSkip: 0 })
        )
      )
    }
  }

  const onLoadMore = (page: any, size: any) => {
    const newSkip = page - 1
    setPageSize(size)
    setSkip(newSkip)
    setCurrentPage(page)
    dispatch(
      viewPendingTransactionsRequest(
        buildParams({ nextPageSize: size, nextSkip: newSkip })
      )
    )
  }

  const onSearch = (values: any) => {
    setIsSearching(true)
    setSkip(0)
    setCurrentPage(1)
    const { merchant, periodFrom, periodTo } = values
    let pFrom: string = '',
      pTo: string = ''
    let m: MerchantData | undefined = undefined

    if (!isEmpty(periodFrom) && !isEmpty(periodTo)) {
      pFrom = moment(periodFrom).format('MM/DD/YYYY')
      pTo = moment(periodTo).format('MM/DD/YYYY')
      setPeriodFrom(pFrom)
      setPeriodTo(pTo)
    }
    
    if (isSuperMerchant && !isEmpty(merchant)) {
      m = merchants.find((m) => m.merchantId === merchant)
      if (m !== undefined) {
        setMerchant(m)
      }
    } else {
      setMerchant(null)
    }

    dispatch(
      viewPendingTransactionsRequest(
        buildParams({
          nextPeriodFrom: pFrom,
          nextPeriodTo: pTo,
          nextMerchant: m || null,
          nextSkip: 0
        })
      )
    )
  }

  const onReset = (form: any) => {
    form.resetFields()
    setPeriodFrom('')
    setPeriodTo('')
    setMerchant(null)
    setSkip(0)
    setCurrentPage(1)
    setPageSize(10)
    setIsSearching(false)
    dispatch(clearData())
  }

  const onExportClick = (type: string) => {
    setExportType(type)
    dispatch(exportViewPendingTxRequest(buildParams({ nextExportType: type })))
  }

  return (
    <div className="padding-box">
      <Content className="site-layout-background site-box">
        <Suspense
          fallback={
            <Row className="suspense-container">
              <div style={{ marginTop: '200px' }}>
                <Spin />
              </div>
            </Row>
          }
        >
          <PendingFilters onReset={onReset} onSearch={onSearch} merchants={merchants} translate={t} />
          {isSearching ? (
            <Fragment>
              {pendingError && (
                <Alert
                  message="Failed to fetch data"
                  description={getErrorMessage(pendingError)}
                  type="error"
                  showIcon
                  style={{ marginTop: 10 }}
                />
              )}
              {pendingExportError && (
                <Alert
                  message="Failed to export data"
                  description={getErrorMessage(pendingExportError)}
                  type="error"
                  showIcon
                  style={{ marginTop: 10 }}
                />
              )}
              <CurrencyFilter onSelectCurrency={onSelectCurrency} translate={t} />
              <div className="margin-top">
                <Row style={{ position: 'relative' }}>
                  <h4 className="transaction-chart-text">{t('general.PendingTransactions')}</h4>
                  <div className="utility-buttons">
                    <>
                      <Button
                        type="primary"
                        className="export-buttons"
                        onClick={() => onExportClick('EXCEL')}
                        loading={isExporting && exportType === 'EXCEL'}
                        disabled={isEmpty(pendingData)}
                      >
                        {t('general.export-excel')}
                      </Button>
                    </>
                    <Button
                      type="primary"
                      className="export-buttons reload"
                      onClick={() => reloadReport()}
                    >
                      {t('general.refresh')}
                    </Button>
                  </div>
                </Row>
                <Details
                  pendingTransactions={pendingData}
                  currency={currency}
                  loading={loading}
                  onLoadMore={onLoadMore}
                  total={total}
                  translate={t}
                  currentPage={currentPage}
                />
              </div>
            </Fragment>
          ) : (
            <EmptyBox header={t('general.filterToGetData')} description="" />
          )}
        </Suspense>
      </Content>
    </div>
  )
}

export default withRouter(PendingTransactionsView)
